import { TranslatedString } from "@gnu-taler/taler-util";
import { ComponentChildren, VNode } from "preact";

/**
 * Properties for form elements set at design type.
 */
export interface UIFormProps<ValType> {
  /**
   * Form file name.
   */
  name: string;

  /**
   * instruction to be shown in the field
   */
  placeholder?: TranslatedString;

  /**
   * long text help to be shown on demand
   */
  tooltip?: TranslatedString;

  /**
   * Short text to be shown next to the field
   */
  help?: TranslatedString;

  /**
   * should show as disabled and readonly
   */
  disabled?: boolean;

  /**
   * should not show
   */
  hidden?: boolean;

  /**
   * should start with focus
   */
  focus?: boolean;

  /**
   * show a mark as required
   */
  required?: boolean;

  /**
   * Label of the field.
   */
  label: TranslatedString;

  /**
   * Untranslated technical identifier shown below the label.
   */
  technicalName?: string;

  before?: Addon;
  after?: Addon;

  /**
   * converter to string and back
   */
  converter?: StringConverter<ValType>;

  handler?: UIFieldHandler<ValType>;
}

/**
 * State shared by every UI field that edits the same form attribute.
 */
export type FormFieldController<T = any> = {
  /** Attribute path in the form result. */
  name: string;

  /** Current attribute value. */
  value: T | undefined;

  onChange: (s: T | undefined) => void;
};

/**
 * State belonging to one occurrence of a field in the form UI.
 */
export type UIFieldHandler<T = any> = {
  field: FormFieldController<T>;

  error?: TranslatedString;

  hidden?: boolean;
};

export function resolveFieldHandler<T>(
  handler: UIFieldHandler<T> | undefined,
  field: string | number | symbol,
): FormFieldController<T> & Pick<UIFieldHandler<T>, "error"> {
  if (!handler) {
    throw Error(
      `Field ${field.toString()} doesn't have handler and is not in a form provider context.`,
    );
  }
  return { ...handler.field, error: handler.error };
}

export interface IconAddon {
  type: "icon";
  icon: VNode;
}

export interface ButtonAddon {
  type: "button";
  onClick: () => void;
  children: ComponentChildren;
}

export interface TextAddon {
  type: "text";
  text: TranslatedString;
}

export type Addon = IconAddon | ButtonAddon | TextAddon;

export interface StringConverter<T> {
  /**
   * Convert the form attribute value to a UI string.
   */
  toStringUI: (v?: T) => string;
  /**
   * Convert a UI string to a form attribute value.
   */
  fromStringUI: (v?: string) => T | undefined;
}
